import {Component, ComponentRef, Input, OnChanges, OnInit, SimpleChanges, Type, ViewContainerRef} from '@angular/core';
import {injectComponent} from '@app/core/utils/inject-component';
import {CellRenderer} from '@shared/interfaces/table/cell-renderer';

/**
 * @internal
 *
 * A dynamic rendering component used internally to inject custom cell renderer components
 * at runtime, based on a passed `cellRenderer` component type.
 *
 * Primarily used in tables or value-detail views where flexible rendering logic is required
 * per field or per column.
 */
@Component({
  selector: 'app-value-data-renderer',
  imports: [],
  templateUrl: './value-data-renderer.component.html',
  styleUrl: './value-data-renderer.component.css'
})
export class ValueDataRendererComponent implements OnInit, OnChanges {
  /**
   * The value to be rendered or passed to the injected cell renderer.
   */
  @Input() public value!: any;

  /**
   * Optional additional parameters for the custom renderer.
   * Typically passed as `cellRendererParams`.
   */
  @Input() public params?: { [key: string]: any };

  /**
   * The Angular component to dynamically inject for rendering the value.
   */
  @Input() public cellRenderer?: Type<any>;

  /**
   * The full row or item data associated with the value (if applicable).
   */
  @Input() public data?: { [key: string]: any };

  /**
   * The key or field name corresponding to the value (e.g., the property being rendered).
   */
  @Input() public key?: any;

  private component: ComponentRef<CellRenderer> = null!;

  /**
   * @param viewContainerRef - The container used to dynamically insert the component.
   */
  constructor(
    private readonly viewContainerRef: ViewContainerRef,
  ) {
  }

  /**
   * Lifecycle hook that runs on initialization.
   * Dynamically injects the specified `cellRenderer` component into the container,
   * passing it `value`, `params`, `data`, and `key` as input bindings.
   */
  public ngOnInit(): void {
    /** Dynamically inject component **/
    if (this.cellRenderer) {
      this.component = injectComponent(
        this.viewContainerRef,
        this.cellRenderer,
        {
          value: this.value,
          params: { key: this.key, cellRendererParams: this.params },
          data: this.data,
        }
      );
    }
  }

  /**
   * Lifecycle hook that runs on changes to the Inputs and forwards changes to the cell renderers
   */
  public ngOnChanges(changes: SimpleChanges): void {
    if (!this.component) {
      return;
    }

    if (changes['value']) {
      this.component.setInput('value', this.value);
    }

    if (changes['data']) {
      this.component.setInput('data', this.data);
    }

    if (changes['key'] || changes['params']) {
      this.component.setInput('params', { key: this.key, cellRendererParams: this.params });
    }
  }
}
